<template>
  <div>
    <v-dialog scrollable v-model="dialogs.delete" persistent max-width="400">
      <material-card
        v-if="selectedItem"
        :title="
          $t('components.employers.employers_stores_dialog.delete_store_{0}', [
            trans(selectedItem, 'Name'),
          ])
        "
        :color="$vuetify.theme.themes.light.error"
        icon="delete"
      >
        <v-card-text>
          <v-form v-model="valid.effective">
            <pbas-date-picker
              :value="effectiveDate"
              @update-date="effectiveDate = $event"
              :label="$t('globals.effective_date')"
              :rules="[$rules.required, $rules.firstRenewalDate]"
              :allowedDates="allowedDates"
              class="required"
            ></pbas-date-picker>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="dialogs.delete = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            text
            @click="tryDelete"
            :disabled="!valid.effective"
          >
            {{ $t("globals.terminate") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <employer-store-wizard-dialog
      v-if="dialogs.edit"
      :employerId="employerId"
      :model="dialogs.edit"
      :id="selectedItem ? selectedItem.Id : undefined"
      @close="close"
    />
    <v-dialog v-model="model" max-width="fit-content" persistent scrollable>
      <material-card
      :title="$t('globals.stores')"
        :color="$vuetify.theme.themes.light.secondary"
        icon="store"
      >
        <v-card-text>
          <pbas-data-table
            :exporters="$exporters"
            ref="table"
            :dataProvider="provider"
            :rowActions="rowActions"
            :floatActions="floatActions"
            fileNamePrefix="stores"
            @itemsChanged="setData($event)"
            :identifier="`employerStoresDialog${selectedPlan.Id}`"
            @error="error = $event"
            :showSelect="false"
            :canExpand="(item) => item.HasHistory"
        :showExpand="true"
        :generateProviderForExpanded="(item) => generateProvider(item)"
          ></pbas-data-table>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="$emit('close')">
            {{ $t("globals.close") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar"
      :color="color"
      :multi-line="mode === 'multi-line'"
      :timeout="timeout"
      :vertical="mode === 'vertical'"
    >
      {{ $t(message) }}
      <v-btn dark text @click="snackbar = false">
        {{ $t("globals.close") }}
      </v-btn>
    </v-snackbar>
    <correct-store-information-dialog
      v-if="selectedItem !== null && selectedItem !== undefined"
      :model="dialogs.correctInfo"
      :id="selectedItem.Id"
      @close="
        dialogs.correctInfo = false;
        selectedItem = null;
        $refs.table.getDataAsync();
      "
    />
    <error-handler :error="error"></error-handler>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ErrorHandler from "@/components/ErrorHandler";
import { ODataProvider } from "pbas-vue-components";
import StoresHeaders from "@/headers/Stores";
import StoresHistoryHeaders from "@/headers/StoresHistory";
import { locker } from "@/mixins/locker";
import { translate2 } from "@/trans2";
import EmployerStoreWizardDialog from "./EmployerStoreWizardDialog.vue";
import CorrectStoreInformationDialog from "@/components/Employers/CorrectStoreInformationDialog.vue";

export default {
  components: {
    ErrorHandler,
    EmployerStoreWizardDialog,
    CorrectStoreInformationDialog,
  },
  mixins: [locker],
  props: ["model", "employerId"],
  data() {
    return {
      items: [],
      error: null,
      provider: undefined,
      saving: false,
      rowActions: [
        {
          name: "editStore",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => this.editItem(item),
          visible: (item) => !this.selectedPlan.Current.IsTesting,
        },
        {
          name: "correctInfo",
          icon: "info",
          title: "globals.correct_information",
          handler: (item) => {
            this.selectedItem = item;
            this.dialogs.correctInfo = true;
          },
          visible: (item) => !this.selectedPlan.Current.IsTesting,
        },
        {
          name: "deleteStore",
          icon: "delete",
          title: "globals.terminate",
          handler: async (item) => {
            try {
              if (this.lockId) {
                this.selectedItem = await this.getStoreById({
                  plan: this.selectedPlan.Id,
                  id: item.Id,
                }).data;
              } else {
                this.selectedItem = await this.lock(
                  this.getStoreById,
                  {
                    plan: this.selectedPlan.Id,
                    id: item.Id,
                    dialogs: this.dialogs,
                    dialog: "delete",
                  },
                  () => {
                    this.dialogs.delete = false;
                  }
                );
              }
              this.dialogs.delete = true;
            } catch (error) {
              this.error = error;
            }
          },
          visible: (item) => !this.selectedPlan.Current.IsTesting,
        },
      ],
      snackbar: false,
      color: "",
      mode: "",
      timeout: 2500,
      message: "",
      dialogs: {
        edit: false,
        delete: false,
        cancel: false,
        correctInfo: false,
      },
      headers: StoresHeaders,
      isNew: false,
      editedItem: null,
      selectedItem: null,
      step: 1,
      valid: {
        storeInfo: false,
        storeDetails: false,
        storeAddress() {
          if (!this.editedStore) return false;
          if (this.editedStore.IsAssignable === false) return true;
          return (
            this.$refs.storeAddress &&
            (this.$refs.storeAddress.valid || !this.$refs.storeAddress.dirty)
          );
        },
        store() {
          if (!this.editedStore) return false;
          return (
            this.storeInfoValid &&
            this.storeDetailsValid &&
            this.storeAddressValid() &&
            this.storeContactsValid()
          );
        },
        effective: false,
      },
      effectiveDate: null,
    };
  },
  computed: {
    ...mapGetters("common", { selectedPlan: "plan" }),
    ...mapGetters("plansMembers", ["options", "planOptions", "planIdentity"]),
    floatActions() {
      if (this.selectedPlan && this.selectedPlan.Current.IsTesting) {
        return [];
      }
      return [
        {
          name: "addStore",
          icon: "add",
          color: this.$vuetify.theme.themes.light.success,
          text: "globals.add_new",
          handler: () => this.addItem(),
        },
      ];
    },
    ...mapGetters("auth", ["isLoggedIn", "user", "role"]),
    ...mapGetters("common", ["language", "settings"]),
  },
  mounted() {
    this.loadPlanData();
    //console.log(this.language);
  },
  methods: {
    ...mapActions("plansMembers", [
      "newStore",
      "updateStore",
      "deleteStore",
      "getStoreById",
      "updateStoreStatus",
    ]),
    allowedDates(val) {
      const n = Date.parse(val);
      const d = new Date(n);
      const n2 = Date.parse(this.planIdentity.FirstRenewalDate);
      const d2 = new Date(n2);
      return d >= d2;
    },
    addItem() {
      this.selectedItem = undefined;
      this.dialogs.edit = true;
    },
    editItem(item) {
      this.selectedItem = item;
      this.dialogs.edit = true;
    },
    setData(items) {
      //console.log("-----------------------set data---------------", items);
      this.items = items;
    },
    reload() {
      this.$refs.table.getDataAsync();
    },
    async loadPlanData() {
      const url = "plans/" + this.selectedPlan.Id + "/stores";
      this.provider = new ODataProvider(this.$axiosHttp, url, this.headers);
      this.provider.filter = `EmployerId eq ${this.employerId}`;
    },
    close() {
      this.dialogs.edit = false;
      this.selectedItem = null;
      this.$refs.table.getDataAsync();
    },
    async tryDelete() {
      this.saving = true;
      try {
        await this.deleteStore({
          planIdentityId: this.selectedPlan.Id,
          storeIdentityId: this.selectedItem.Id,
          payload: {EffectiveDate: this.effectiveDate},
        });
        this.color = "success";
        this.message = "globals.success";
        this.snackbar = true;
        this.dialogs.delete = false;
        this.effectiveDate = null;
        if (this.$refs.table) this.$refs.table.getDataAsync();
      } catch (error) {
        this.error = error;
      }
      this.saving = false;
    },
    cancelStoreWizard() {
      //console.log("CANCEL STORE");
      // this.contact = JSON.parse(JSON.stringify(this.defaultContact));
      if (this.$refs.contactform) this.$refs.contactform.resetValidation();
      this.$refs.storeInfoForm.resetValidation();
      if (this.$refs.contactform) this.$refs.storeDetailsForm.resetValidation();
      this.dialogs.cancel = false;
      this.dialogs.edit = false;
      this.step = 1;
      this.closeStore();
    },
    trans(item, field) {
      return translate2(item, field);
    },
    continueToStoreAddress() {
      if (this.$refs.storeAddress) {
        this.$refs.storeAddress.validate();
      }
      this.step = 3;
    },
    storeAddressBack() {
      if (this.$refs.storeAddress) {
        this.$refs.storeAddress.resetValidation();
      }
      this.step = 2;
    },
    generateProvider(item) {
      const url =
        "plans/" + this.selectedPlan.Id + "/stores/" + item.Id + "/history";
      return new ODataProvider(this.$axiosHttp, url, StoresHistoryHeaders);
    },
  },
};
</script>
